import type { CallTranscriptMessage, TranscriptSnippet } from "@expert/shared-types";

import { removePii } from "@asurion-core/expert-workspace-pii";
import { addMessage, DEAD_AIR_TAG, useTimelineStore } from "@asurion-core/expert-workspace-timeline";
import {
    type GaiaWebSocketResponse,
    gaiaWsEventBus,
    getFallbackPartner,
    getSupportedLineOfBusiness,
    useGaiaStore,
    useGaiaWebsocket,
} from "@expert/gaia";
import { getLogger } from "@expert/logging";
import { triggerProactiveSound, triggerTabNotification } from "@expert/proactive-notifications";
import { useEffect, useEffectEvent, useRef } from "react";

import { getRootDispatcher } from "@/analytics";
import { isVoiceTask, useActiveTask } from "@/sdk";
import { useFeatureFlag } from "@/shared-utils/hooks";
import { mergeSnippets } from "@/transcript/utils/mergeSnippets";

import { getAnswerGateRejection } from "./deadAirAnswerGate";
import {
    DEAD_AIR_APP_NAME,
    DEAD_AIR_ASK_COOLDOWN_MS,
    DEAD_AIR_CONFIDENCE_THRESHOLD,
    DEAD_AIR_DARK_LAUNCH_FLAG_KEY,
    DEAD_AIR_DARK_LAUNCH_TRANSCRIPT_TAIL,
    DEAD_AIR_FLAG_KEY,
    DEAD_AIR_TRANSCRIPT_WINDOW,
} from "./deadAirConfig";
import { isFillableCategory } from "./deadAirTaxonomy";
import { requestDeadAirClassification } from "./requestDeadAirClassification";

const logger = getLogger({ module: "useDeadAirAssist" });

/**
 * Watches the live voice transcript for in-call silence ("dead air") and, when the agent has been quiet
 * past the configured threshold while the customer spoke last, silently asks Expert Assist why. Only
 * when the model is confident the agent is looking up something it can answer (a fillable category) does
 * a single proactive suggestion appear in the timeline. Detection, the GAIA round-trip, and all
 * telemetry are invisible to the expert — the surfaced suggestion is the only thing they ever see.
 *
 * Design notes:
 * - The provider-agnostic seam is the GAIA `gaia_ws_call-transcript` event (same envelope
 *   `useTranscriptSnippetListener` consumes), so this covers both Amazon Connect and Twilio calls.
 * - Silence is measured with the wall clock: a timer is armed on the customer's latest snippet and
 *   fires if no further snippet arrives before the threshold.
 * - Enablement is the config's presence: the threshold comes from `EWP-deadAirTimeout` (seconds); a
 *   missing/zero value disables the feature. Mount once via <DeadAirAssist/>.
 * - Dark launch: when `EWP-isDeadAirDarkLaunchEnabled` is true, everything runs as above except the
 *   surfacing step — the suggestion is withheld (no timeline message, tab notification, or sound) and
 *   `DeadAirAssistWouldHaveSurfaced` records what the expert would have seen, and when.
 */
/**
 * The dark-launch event lands in EDP analytics, so customer speech (and an answer that may quote it, e.g.
 * RE_ASK) is scrubbed first. If redaction fails, nothing raw is sent: the answer is null and the tail empty.
 */
async function scrubForDarkLaunch(answer: string, tail: TranscriptSnippet[]) {
    try {
        const [scrubbedAnswer, ...scrubbedTail] = await Promise.all([
            removePii(answer),
            ...tail.map(({ content }) => removePii(content)),
        ]);
        return {
            answer: scrubbedAnswer,
            transcriptTail: tail.map(({ speaker }, i) => ({ speaker, text: scrubbedTail[i] })),
        };
    } catch (error) {
        logger.warn({ error }, "Dead-air dark-launch PII redaction failed; sending the event without text");
        return { answer: null, transcriptTail: [] };
    }
}

export function useDeadAirAssist(): void {
    const { data: timeoutSeconds } = useFeatureFlag<number>(DEAD_AIR_FLAG_KEY);
    const thresholdMs = typeof timeoutSeconds === "number" && timeoutSeconds > 0 ? timeoutSeconds * 1000 : null;
    const {
        data: darkLaunchFlag,
        isLoading: isDarkLaunchLoading,
        isError: isDarkLaunchError,
    } = useFeatureFlag<boolean>(DEAD_AIR_DARK_LAUNCH_FLAG_KEY);
    // Fail closed: if the dark-launch flag has not resolved (or failed to), withhold rather than risk
    // showing a dark-launch expert a suggestion. A resolved-but-absent flag still means live.
    const isDarkLaunch = darkLaunchFlag === true || isDarkLaunchLoading || isDarkLaunchError;

    useEffect(() => {
        if (isDarkLaunchError) {
            logger.warn("Dead-air dark-launch flag errored; failing closed and withholding suggestions");
        }
    }, [isDarkLaunchError]);

    const websocket = useGaiaWebsocket();
    const activeTask = useActiveTask();
    const activeVoiceTask = isVoiceTask(activeTask) ? activeTask : null;
    const activeCallSid = activeVoiceTask?.status === "assigned" ? activeVoiceTask.agentCallId : undefined;

    // Mutable refs so the effect-events always read fresh state without re-subscribing the bus.
    const snippetsRef = useRef<TranscriptSnippet[] | undefined>(undefined);
    const lastArrivalRef = useRef(0);
    const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlightRef = useRef(false);
    const lastAskAtRef = useRef(0);
    const abortRef = useRef<AbortController | null>(null);

    const clearSilenceTimer = () => {
        if (silenceTimerRef.current !== null) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
    };

    const fireDeadAirAsk = useEffectEvent(async () => {
        silenceTimerRef.current = null;
        if (thresholdMs === null || inFlightRef.current || !activeCallSid || !websocket) return;

        const { currentSessionId: sessionId, expertId, getTimelineState } = useTimelineStore.getState();
        if (!sessionId) return;
        const { callSid, partner, lob } = getTimelineState(sessionId);
        // Only act on the live customer call this timeline belongs to.
        if (callSid !== activeCallSid) return;
        if (useGaiaStore.getState().subscriptionStatus[sessionId] !== "subscribed") {
            logger.debug({ sessionId }, "Dead-air ask skipped: session not subscribed to GAIA");
            return;
        }

        const transcriptWindow = (snippetsRef.current ?? []).slice(-DEAD_AIR_TRANSCRIPT_WINDOW);
        if (transcriptWindow.length === 0) return;

        const askedAt = Date.now();
        const gapSeconds = Math.round((askedAt - lastArrivalRef.current) / 1000);
        const formattedPartner = getFallbackPartner({ partner });

        inFlightRef.current = true;
        const abort = new AbortController();
        abortRef.current = abort;

        void getRootDispatcher().dispatchBusinessEvent("DeadAirDetected", {
            callSid: activeCallSid,
            partner: formattedPartner,
            gapSeconds,
            thresholdSeconds: Math.round(thresholdMs / 1000),
            darkLaunch: isDarkLaunch,
        });

        let classification = null;
        try {
            classification = await requestDeadAirClassification({
                transcript: transcriptWindow,
                gapSeconds,
                context: {
                    appName: DEAD_AIR_APP_NAME,
                    partner: formattedPartner,
                    sessionId,
                    callSid: activeCallSid,
                    expertId,
                    lineOfBusiness: getSupportedLineOfBusiness({ partner: formattedPartner, lob }),
                },
                sendJsonMessage: websocket.sendJsonMessage,
                logger,
                signal: abort.signal,
            });
        } catch (error) {
            logger.warn({ error }, "Dead-air classification request threw");
        }

        abortRef.current = null;
        lastAskAtRef.current = Date.now();
        inFlightRef.current = false;

        // A newer call started (or this one ended) while we awaited — drop the stale answer.
        if (abort.signal.aborted) return;

        const isConfidentFill =
            classification !== null &&
            classification.answer !== null &&
            isFillableCategory(classification.category) &&
            classification.confidence >= DEAD_AIR_CONFIDENCE_THRESHOLD;
        // Even a confident fill only surfaces in the shapes the dark-launch review found useful.
        const gateReason =
            isConfidentFill && classification?.answer
                ? getAnswerGateRejection(classification.category, classification.answer)
                : null;
        const shouldSurface = isConfidentFill && gateReason === null;

        if (shouldSurface && classification?.answer && isDarkLaunch) {
            // Dark launch: withhold the suggestion and record what (and when) the expert would have seen,
            // so calls can be reviewed for whether it would have helped before it is shown to anyone.
            const wouldHaveSurfacedAt = Date.now();
            const { answer, transcriptTail } = await scrubForDarkLaunch(
                classification.answer,
                transcriptWindow.slice(-DEAD_AIR_DARK_LAUNCH_TRANSCRIPT_TAIL),
            );
            void getRootDispatcher().dispatchBusinessEvent("DeadAirAssistWouldHaveSurfaced", {
                callSid: activeCallSid,
                partner: formattedPartner,
                category: classification.category,
                confidence: classification.confidence,
                answer,
                transcriptTail,
                gapSeconds,
                thresholdSeconds: Math.round(thresholdMs / 1000),
                silenceStartedAt: new Date(lastArrivalRef.current).toISOString(),
                wouldHaveSurfacedAt: new Date(wouldHaveSurfacedAt).toISOString(),
                classificationLatencyMs: wouldHaveSurfacedAt - askedAt,
            });
        } else if (shouldSurface && classification?.answer) {
            addMessage(sessionId, {
                id: `deadAir_${crypto.randomUUID()}`,
                text: classification.answer,
                type: "Proactive",
                tag: DEAD_AIR_TAG,
                isUnread: true,
                status: "active",
                timestamp: new Date().toISOString(),
            });
            // Match the notification behaviour every other Proactive message already gets (see
            // useGaiaMessageEvents). Dead air fires precisely when the expert has gone quiet, which is
            // when they are most likely to be in another tab or system, so a silent badge-less
            // suggestion is the one most likely to be missed. Dead-air's own config is the enable gate
            // here; the expert's sound preference is still honoured inside triggerProactiveSound.
            triggerTabNotification("Expert Workspace", true);
            triggerProactiveSound(true);
            void getRootDispatcher().dispatchBusinessEvent("DeadAirAssistSurfaced", {
                callSid: activeCallSid,
                partner: formattedPartner,
                category: classification.category,
                confidence: classification.confidence,
                gapSeconds,
            });
        } else {
            // In the dark launch, a gated answer carries its scrubbed text so the gate's own false
            // negatives can be graded the same way would-have-surfaced suggestions are.
            const gatedContent =
                gateReason !== null && isDarkLaunch && classification?.answer
                    ? await scrubForDarkLaunch(
                          classification.answer,
                          transcriptWindow.slice(-DEAD_AIR_DARK_LAUNCH_TRANSCRIPT_TAIL),
                      )
                    : undefined;
            void getRootDispatcher().dispatchBusinessEvent("DeadAirAssistSuppressed", {
                callSid: activeCallSid,
                partner: formattedPartner,
                gapSeconds,
                darkLaunch: isDarkLaunch,
                category: classification?.category,
                confidence: classification?.confidence,
                reason:
                    classification === null
                        ? "no-classification"
                        : classification.answer === null
                          ? "no-answer"
                          : !isFillableCategory(classification.category)
                            ? "not-fillable"
                            : gateReason !== null
                              ? "answer-gated"
                              : "low-confidence",
                gateReason: gateReason ?? undefined,
                ...gatedContent,
            });
        }
    });

    const onSnippet = useEffectEvent((event: GaiaWebSocketResponse) => {
        if (thresholdMs === null || !activeCallSid) {
            clearSilenceTimer();
            return;
        }

        const body = event.body as CallTranscriptMessage;
        // Scope to the active call; drops trailing snippets from a prior/ended call.
        if (body.sessionId !== activeCallSid) return;
        if (!body.transcripts || body.transcripts.length === 0) return;

        const merged = mergeSnippets(snippetsRef.current, body.transcripts);
        snippetsRef.current = merged;
        if (!merged || merged.length === 0) return;
        const latest = merged.at(-1);

        // Any fresh snippet ends the current silence watch. Re-arm whenever the customer spoke last
        // (agent-silent = the fillable case) and we're not mid-ask. The cooldown must not gate arming:
        // a snippet arriving during it would clear the timer with nothing left to re-arm it, silently
        // losing that dead-air window. Instead wait for whichever of the two elapses last, so a
        // threshold shorter than the cooldown still yields exactly one ask per silence.
        clearSilenceTimer();
        if (latest?.speaker === "Customer" && !inFlightRef.current) {
            const now = Date.now();
            lastArrivalRef.current = now;
            const cooldownRemainingMs = lastAskAtRef.current + DEAD_AIR_ASK_COOLDOWN_MS - now;
            const delayMs = Math.max(thresholdMs, cooldownRemainingMs);
            silenceTimerRef.current = setTimeout(() => void fireDeadAirAsk(), delayMs);
        }
    });

    // Reset per-call detection state whenever the active call changes (new call or call ended).
    useEffect(() => {
        snippetsRef.current = undefined;
        lastArrivalRef.current = 0;
        lastAskAtRef.current = 0;
        inFlightRef.current = false;
        clearSilenceTimer();
        abortRef.current?.abort();
        abortRef.current = null;
    }, [activeCallSid]);

    useEffect(() => {
        const unsubscribe = gaiaWsEventBus.on("gaia_ws_call-transcript", onSnippet);
        return () => {
            unsubscribe();
            clearSilenceTimer();
            abortRef.current?.abort();
        };
        // Stable subscription: onSnippet is an effect-event and reads the latest refs/props internally.
        // oxlint-disable-next-line exhaustive-deps
    }, []);
}
